import { canPromoteInPlace } from "./fundedLineage";
import { MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../client/components/ui/dropdown-menu";
import type { EnrichedAccount, EnrichedGroup } from "./accountTypes";
import AccountSourceBadge from "./AccountSourceBadge";
import BalanceFreshnessHint from "./BalanceFreshnessHint";

interface AccountTableProps {
  accounts: EnrichedAccount[];
  groups: EnrichedGroup[];
  onEdit: (a: EnrichedAccount) => void;
  onDelete: (a: EnrichedAccount) => void;
  onToggleActive: (a: EnrichedAccount) => void;
  onRequestDeactivate: (a: EnrichedAccount) => void;
  onOpenDetail: (a: EnrichedAccount) => void;
  onFundedTransition: (a: EnrichedAccount) => void;
  isPro?: boolean;
  onConnect?: (accountId: string) => void;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}


function ProgressBar({
  ratio,
  isWarning,
  isViolation,
}: {
  ratio: number;
  isWarning: boolean;
  isViolation: boolean;
}) {
  const pct = Math.min(Math.round(ratio * 100), 100);
  const color = isViolation ? "bg-red-500" : isWarning ? "bg-yellow-400" : "bg-primary";
  return (
    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function AccountTable({
  accounts,
  onEdit,
  onDelete,
  onToggleActive,
  onRequestDeactivate,
  onOpenDetail,
  onFundedTransition,
}: AccountTableProps) {
  const t = useTranslation();
  const navigate = useNavigate();
  const ta = t.tradingAccounts;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 hover:bg-transparent">
              <th className="w-[200px] px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colName}
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colStatus}
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colPropFirm}
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colGroup}
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colSource}
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colStrategy}
              </th>
              <th className="px-4 py-3 text-right text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colBalance}
              </th>
              <th className="px-4 py-3 text-right text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colPnl}
              </th>
              <th className="px-4 py-3 text-right text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {ta.colTrades}
              </th>
              <th className="w-[48px] px-2 py-3" />
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => {
              const pnl = account.currentBalance - account.initialBalance;
              const propFirmLabel = account.effectiveTemplate?.name ?? "—";
              const groupName = account.group?.name ?? "—";

              return (
                <tr
                  key={account.id}
                  className={`border-b border-border/40 last:border-0 cursor-pointer transition-colors hover:bg-accent/30 ${
                    !account.isActive ? "opacity-60" : ""
                  }`}
                  onClick={() => navigate(`/account/${account.id}`)}
                >
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full shrink-0 ${
                          account.status === "pa" || account.status === "live"
                            ? "bg-primary"
                            : account.status === "challenge"
                            ? "bg-yellow-400"
                            : account.status === "demo"
                            ? "bg-blue-400"
                            : "bg-muted-foreground"
                        }`}
                      />
                      <span className="truncate max-w-[160px]">{account.name}</span>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="text-xs capitalize text-muted-foreground">
                      {account.status}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {propFirmLabel}
                  </td>

                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {groupName}
                  </td>

                  <td className="px-4 py-3">
                    <AccountSourceBadge account={account as any} />
                  </td>

                  <td className="px-4 py-3">
                    {account.strategies && account.strategies.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {account.strategies.map((s) => (
                          <span
                            key={s.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${s.color}22`, color: s.color, border: `1px solid ${s.color}44` }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                            {s.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3 text-right font-mono text-sm tabular-nums">
                    <div className="flex flex-col items-end gap-0.5">
                      {formatCurrency(account.currentBalance, account.currency)}
                      <BalanceFreshnessHint
                        brokerBalance={(account as any).brokerBalance}
                        brokerBalanceAt={(account as any).brokerBalanceAt}
                      />
                    </div>
                  </td>

                  <td
                    className={`px-4 py-3 text-right font-mono text-sm tabular-nums ${
                      pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"
                    }`}
                  >
                    {pnl >= 0 ? "+" : ""}
                    {formatCurrency(pnl, account.currency)}
                  </td>

                  <td className="px-4 py-3 text-right text-sm tabular-nums">
                    {account.tradesCount}
                  </td>

                  <td
                    className="px-2 py-3"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onOpenDetail(account)}>
                          {ta.viewAccountDetail}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(account)}>
                          {ta.editAccount}
                        </DropdownMenuItem>
                        {canPromoteInPlace(account) && (
                          <DropdownMenuItem onClick={() => onFundedTransition(account)}>
                            {ta.promoteToFunded}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            if (account.isActive) {
                              onRequestDeactivate(account);
                            } else {
                              onToggleActive(account);
                            }
                          }}
                        >
                          {account.isActive ? ta.deactivateAccount : ta.activateAccount}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => onDelete(account)}
                        >
                          {ta.confirmDelete}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
